"use client";

import { useEffect, useRef } from "react";

/** Keep hero frames moving at the homepage's slower scroll rate. */
export function useCcHeroScrollParallax(enabled = true) {
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const section = sectionRef.current;
    if (!section || !enabled) return;
    // Native touch scrolling runs on the compositor. A JS-transformed full hero
    // cannot reliably keep up with it, especially during momentum scrolling.
    const nativeScroll = window.matchMedia("(max-width: 767px), (pointer: coarse), (prefers-reduced-motion: reduce)");
    let frame = 0;
    let previous = "";
    const update = () => {
      frame = 0;
      const rect = section.getBoundingClientRect();
      const offset = (Math.min(rect.height, Math.max(0, -rect.top)) * 0.6).toFixed(2);
      if (offset === previous) return;
      previous = offset;
      section.style.setProperty("--cc-hero-scroll-offset", `${offset}px`);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    const configure = () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.cancelAnimationFrame(frame);
      frame = 0;
      previous = "";
      section.style.removeProperty("--cc-hero-scroll-offset");
      if (nativeScroll.matches) return;
      window.addEventListener("scroll", schedule, { passive: true });
      window.addEventListener("resize", schedule);
      schedule();
    };
    configure();
    nativeScroll.addEventListener("change", configure);
    return () => {
      nativeScroll.removeEventListener("change", configure);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.cancelAnimationFrame(frame);
      section.style.removeProperty("--cc-hero-scroll-offset");
    };
  }, [enabled]);

  return sectionRef;
}
